import { Text, TextColor, TextSize, TextWeight } from '@vizit/atoms';
import { WarningTriangleIcon } from '@vizit/icons';
import { cx } from '@vizit/theme';
import { plural } from '@vizit/utils';
import { AffectedCollectionList } from '../benchmark-delete-dialog/affected-collection-list';
import { DeleteImagesDialog, type DeleteImagesDialogProps } from '../delete-images-dialog';

export interface BenchmarkRemoveImagesDialogProps
  extends Omit<DeleteImagesDialogProps, 'title' | 'description' | 'confirmLabel' | 'children'> {
  /**
   * How many images the benchmark has before the removal
   */
  imageCount: number;
  /**
   * Images the benchmark needs to score collections, as the API reports it
   */
  minimumImages: number;
  /**
   * Collections scored against the benchmark, by name; leave unset while they are unknown
   *
   * @optional
   */
  affectedCollections?: { id: string; name: string }[];
  /**
   * How many collections are scored against the benchmark, as the benchmark itself reports it, so the notice needs no
   * lookup; `affectedCollections` names the first of them as they load. Defaults to the number named
   *
   * @optional
   */
  affectedCollectionCount?: number;
}

/**
 * Confirms removing images from a benchmark. The images stay in the library, so the copy says they leave this
 * benchmark only. When collections are scored against it, a notice lists them: their scores stay as they are, and
 * when the removal leaves the benchmark under its minimum, it warns they can't score anything new against it until it
 * has enough images again. With no collection, a removal that leaves it under the minimum says how many images are left.
 */
export function BenchmarkRemoveImagesDialog(props: BenchmarkRemoveImagesDialogProps) {
  const { count, imageCount, minimumImages, affectedCollections = [], affectedCollectionCount, ...other } = props;

  const isOne = count === 1;
  const remaining = Math.max(imageCount - count, 0);
  const isUnderMinimum = remaining < minimumImages;
  // Whichever source knows more: a name listed counts even if the benchmark's own count has not caught up.
  const affectedCount = Math.max(affectedCollectionCount ?? 0, affectedCollections.length);
  const isOneCollection = affectedCount === 1;
  const left = `It will have ${plural(remaining, 'image')}`;
  const theCollections = isOneCollection ? 'The collection' : `The ${affectedCount} collections`;
  const keepScores = isOneCollection ? 'keeps its current scores' : 'keep their current scores';

  return (
    <DeleteImagesDialog
      {...other}
      count={count}
      title={isOne ? 'Remove 1 image' : `Remove ${count} images`}
      description={
        <>
          {isOne ? 'This image is' : 'These images are'} removed from this benchmark only.
          {isUnderMinimum &&
            affectedCount === 0 &&
            ` ${left}, under the ${minimumImages} it needs to score collections.`}
        </>
      }
      confirmLabel="Remove"
    >
      {affectedCount > 0 && (
        <div
          className={cx(
            'flex flex-col gap-4 border p-6',
            isUnderMinimum ? 'border-warning-200 bg-warning-50' : 'border-gray-200 bg-gray-50',
          )}
        >
          <div className={cx('flex items-center gap-3', isUnderMinimum ? 'text-warning-700' : 'text-primary')}>
            {isUnderMinimum && <WarningTriangleIcon size={16} aria-hidden />}
            <Text size={TextSize.Sm} weight={TextWeight.Semibold} className="text-inherit">
              {isUnderMinimum
                ? `${imageCount < minimumImages ? 'Leaves' : 'Takes'} it below ${minimumImages} images`
                : `Used by ${plural(affectedCount, 'collection')}`}
            </Text>
          </div>
          <Text size={TextSize.Sm} color={TextColor.Secondary} showEllipsis={false}>
            {isUnderMinimum
              ? `${left}. ${theCollections} scored against it ${keepScores}, but can't score new categories or uploads until it has ${minimumImages} again.`
              : `${isOneCollection ? 'Its' : 'Their'} current scores stay as they are.`}
          </Text>
          <AffectedCollectionList collections={affectedCollections} count={affectedCount} />
        </div>
      )}
    </DeleteImagesDialog>
  );
}
